import {
  Grid,
  Typography,
  Box,
  FormControlLabel,
  RadioGroup,
  useMediaQuery,
} from '@mui/material'
import { useMemo } from 'react'
import * as yup from 'yup'
import { Form, Formik, FormikErrors, FormikState } from 'formik'
import { useTranslation } from 'react-i18next'
import { useConfig } from 'hooks/useConfig'
import Input from 'components/Input/Input'
import Modal from 'components/Modal/Modal'
import { StyledInput } from 'components/Input/Input.styled'
import { usePostOrderRefund } from 'hooks/useOrders'
import theme from 'lib/theme'
import { Product, ExtraProduct } from 'types/model/product'
import { useAlert } from 'providers/AlertProvider'
import { AxiosError } from 'axios'
import { productInfoForRefunds } from 'lib/utils'
import { RadioBtn } from './OrderProduct.styled'

type OrderProductModalProps = {
  partId: string
  orderId: string
  paymentStatus: string
  modal: { right: boolean }
  handleCloseModal: () => void
  products: Product[]
  productInfo: {
    gtin: ''
    extId: ''
    id: ''
    name: ''
    details: { brandName: ''; name: '' }
  }[]
  paymentType: string
  currency: string
}

type ValueProps = {
  initialProducts: {
    refundableValue: number
    amount: string
    quantity: string
    orderedQuantity: number
  }[]
  message: string
  type: string
}

export const OrderProductModal = ({
  partId,
  orderId,
  modal,
  handleCloseModal,
  products,
  productInfo,
  paymentType,
  currency,
  paymentStatus,
}: OrderProductModalProps) => {
  const { t } = useTranslation()
  const config = useConfig('refunds') || undefined

  const { mutateAsync, isLoading: isLoadingRequest } = usePostOrderRefund(
    orderId as string,
    partId as string
  )
  const { setAlert } = useAlert()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const onlyChargedProducts = products.filter((product) => {
    if (paymentStatus) {
      return (
        product.refundable &&
        paymentStatus === 'captured' &&
        Object.keys(product.collectedProduct).length > 0 &&
        product.collectedProduct.capturedPrice.priceVat > 0
      )
    }
    return (
      !product.refundable &&
      product.collectedProduct &&
      Object.keys(product.collectedProduct).length > 0 &&
      product.collectedProduct.capturedPrice.priceVat > 0
    )
  })

  const handleQuantityChange = (
    refundUnit: string,
    orderedQuantity: number,
    index: number,
    rowPrice: number,
    setFieldValue: (field: string, value: string) => void
  ) => {
    const units = Number(refundUnit)
    if (Number.isNaN(units)) {
      return
    }
    const refundAmount = ((rowPrice / orderedQuantity) * units).toFixed(2)
    setFieldValue(`initialProducts.${index}.quantity`, refundUnit)
    setFieldValue(`initialProducts.${index}.amount`, refundAmount)
  }

  const handleAmountChange = (
    amount: string,
    index: number,
    setFieldValue: (field: string, value: string) => void
  ) => {
    setFieldValue(`initialProducts.${index}.amount`, amount)
  }

  const initialValues = useMemo(() => {
    return {
      initialProducts:
        onlyChargedProducts.length > 0
          ? onlyChargedProducts.map((product) => {
              const { quantity, refundableValue, orderedQuantity } =
                productInfoForRefunds(product)
              return {
                refundableValue,
                orderedQuantity,
                amount:
                  refundableValue > 0
                    ? refundableValue.toFixed(2).toString()
                    : '',
                quantity: refundableValue > 0 ? quantity?.toString() : '',
              }
            })
          : [
              {
                orderedQuantity: NaN,
                amount: '',
                refundableValue: NaN,
                quantity: '',
              },
            ],
      message: '',
      type: paymentType === 'offline' ? 'manual' : 'automatic',
    }
  }, [onlyChargedProducts, paymentType])

  const validationSchema = useMemo(() => {
    return yup.object({
      initialProducts: yup.array().of(
        yup.object().shape({
          quantity: yup
            .number()
            .typeError(t('mustBeNumeric'))
            .positive(t('mustBePositive'))
            .max(
              yup.ref('orderedQuantity'),
              t('orderProduct.quantityExceedsOrdered')
            )
            .when([], {
              is: () => config?.qty_type && config.qty_type === 'int',
              then: (schema) => schema.integer(t('mustBeInt')),
            })
            .when([], {
              is: () => config?.require_qty,
              then: (schema) => schema.required(t('required')),
            })
            .when([], {
              is: () => config?.refund_type && config?.refund_type === 'value',
              then: (schema) => schema.notRequired(),
            })
            .when([], {
              is: () => config?.refund_type && config?.refund_type === 'both',
              then: (schema) => schema.required(t('required')),
            }),
          amount: yup
            .number()
            .typeError(t('mustBeNumeric'))
            .positive(t('mustBePositive'))
            .required(t('required'))
            .max(
              yup.ref('refundableValue'),
              t('orderProduct.amountExceedsCharged')
            ),
          refundableValue: yup.number(),
        })
      ),
      message:
        config?.require_reason === false
          ? yup.string().trim().notRequired()
          : yup.string().trim().required(t('required')).min(1, t('required')),
      type: yup.string().required(t('required')),
    })
  }, [config, t])

  const handleSubmit = async (
    values: ValueProps,
    resetForm: (nextState?: Partial<FormikState<ValueProps>>) => void
  ) => {
    const refunds = {
      refunds: values.initialProducts.map((p, index) => {
        return {
          row_id: Number(onlyChargedProducts[index].id),
          message: values.message,
          type: values.type,
          amount: Number(p.amount),
          ...(p.quantity && { quantity: Number(p.quantity) }),
        }
      }),
    }
    await mutateAsync(refunds, {
      onSuccess: () => {
        handleCloseModal()
        setAlert(t('saved'), 'success', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
        resetForm({ values })
      },
      onError: (error) => {
        const resp = (
          error as unknown as AxiosError<{
            error: { message: string }
          }>
        ).response

        setAlert(
          resp?.status === 422
            ? t('incorrectInput')
            : t('refundFailureMessage', {
                error: resp?.data?.error?.message ?? '',
              }),
          'failure',
          {
            vertical: 'bottom',
            horizontal: isMobile ? 'center' : 'right',
          }
        )
      },
    })
  }

  const isNonRefundableProduct = (product: Product) => {
    return (
      product.refundable?.refundable === false ||
      product.refundable?.rowRefundableValue === 0 ||
      product.extraProduct === ExtraProduct.DISCOUNT
    )
  }
  return (
    <Formik
      enableReinitialize
      validateOnMount
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { resetForm }) =>
        handleSubmit(values, resetForm)
      }>
      {({
        setFieldValue,
        values,
        errors,
        isValid,
        submitForm,
        handleBlur,
        touched,
      }) => (
        <Modal
          showButton
          setModal={handleCloseModal}
          modal={modal}
          isLoadingRequest={isLoadingRequest}
          buttonText={t('continue')}
          onButtonClick={submitForm}
          buttonIsDisabled={!isValid}
          title={t('orderProduct.refund')}>
          <Grid
            container
            sx={{
              borderBottom: `2px solid ${theme.palette.action.disabled}`,
              paddingBottom: theme.spacing(1),
              marginBottom: theme.spacing(1),
            }}>
            <Grid item xs={4}>
              <Typography
                variant='body2'
                sx={{ color: theme.palette.text.secondary }}>
                {t('product').toUpperCase()}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography
                variant='body2'
                sx={{ color: theme.palette.text.secondary }}>
                {t('orderProduct.quantity').toUpperCase()}
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography
                variant='body2'
                sx={{ color: theme.palette.text.secondary }}>
                {t('orderProduct.rowPrice').toUpperCase()}
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography
                variant='body2'
                sx={{ color: theme.palette.text.secondary }}>
                {t('orderProduct.refundable').toUpperCase()}
              </Typography>
            </Grid>
          </Grid>

          <Form
            style={{
              width: '100%',
            }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}>
              <Box>
                {products &&
                  productInfo &&
                  products.map((product) => {
                    const details = productInfo.filter(
                      (p) =>
                        p.extId === product.collectedProduct?.sku ||
                        p.gtin.toString() ===
                          product.collectedProduct?.gtin.toString()
                    )[0]
                    const index = onlyChargedProducts.indexOf(product)
                    const productQuantity = product.collectedProduct?.quantity

                    const invalidCollectedProduct =
                      !product.collectedProduct ||
                      Object.keys(product.collectedProduct).length === 0

                    const showAmountExceededError =
                      touched.initialProducts?.[index]?.amount ||
                      touched.initialProducts?.[index]?.quantity
                    const productError = (
                      errors.initialProducts as FormikErrors<
                        ValueProps['initialProducts']
                      >
                    )?.[index]
                    return (
                      <Grid
                        container
                        rowSpacing={2}
                        columnSpacing={0.5}
                        key={product.id}
                        sx={{
                          borderBottom: `1px solid ${theme.palette.action.disabled}`,
                          paddingBottom: theme.spacing(2),
                          marginBottom: theme.spacing(3),
                        }}>
                        <Grid item xs={4}>
                          {details && (
                            <div>
                              <Typography>
                                {details.name
                                  ? details.name
                                  : details.details.name}
                              </Typography>
                              <Typography
                                sx={{
                                  color: theme.palette.text.secondary,
                                  marginBottom: theme.spacing(3),
                                }}>
                                {details.details.brandName}
                              </Typography>
                            </div>
                          )}
                        </Grid>
                        <Grid item xs={4}>
                          <Typography>{productQuantity}</Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <Typography>
                            {invalidCollectedProduct
                              ? product?.rowPrice?.priceVat.toFixed(2)
                              : product.collectedProduct?.rowPrice?.priceVat.toFixed(
                                  2
                                )}
                          </Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <Typography>
                            {invalidCollectedProduct ||
                            isNonRefundableProduct(product)
                              ? '-'
                              : (product.refundable?.rowRefundableValue.toFixed(
                                  2
                                ) || // Use refundable row price if available
                                  product.collectedProduct?.capturedPrice?.priceVat.toFixed(
                                    2
                                  )) ??
                                ''}
                          </Typography>
                        </Grid>
                        <Grid item xs={4} />
                        <Grid item xs={4}>
                          <StyledInput
                            variant='filled'
                            onBlur={handleBlur}
                            onChange={(e) => {
                              handleQuantityChange(
                                e.target.value,
                                productQuantity,
                                index,
                                product.refundable?.rowRefundableValue ||
                                  product.collectedProduct?.rowPrice?.priceVat,
                                setFieldValue
                              )
                            }}
                            value={values.initialProducts?.[index]?.quantity}
                            error={Boolean(productError?.quantity)}
                            helperText={productError?.quantity ?? ''}
                            name={`initialProducts.${index}.quantity`}
                            onKeyDown={(e) => {
                              if (
                                config &&
                                config.qty_type === 'int' &&
                                (e.key === '.' || e.key === '-')
                              ) {
                                e.preventDefault()
                              }
                            }}
                            label={t('refundUnits')}
                            type='text'
                            sx={{
                              marginBottom: 0,
                              '& .MuiFormHelperText-root': {
                                position: 'static',
                              },
                            }}
                            disabled={isNonRefundableProduct(product)}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <StyledInput
                            variant='filled'
                            onBlur={handleBlur}
                            value={values.initialProducts?.[index]?.amount}
                            onChange={(e) =>
                              handleAmountChange(
                                e.target.value,
                                index,
                                setFieldValue
                              )
                            }
                            error={Boolean(
                              productError?.amount && showAmountExceededError
                            )}
                            helperText={
                              (showAmountExceededError &&
                                productError?.amount) ??
                              ''
                            }
                            sx={{
                              marginBottom: 0,
                              '& .MuiFormHelperText-root': {
                                position: 'static',
                              },
                            }}
                            label={t('refund', { currency })}
                            type='text'
                            disabled={
                              config?.refund_type === 'quantity' ||
                              isNonRefundableProduct(product)
                            }
                            name={`initialProducts.${index}.amount`}
                            inputProps={{
                              'data-testid': 'refundAmountInput',
                            }}
                          />
                        </Grid>
                      </Grid>
                    )
                  })}
                <Box display='flex' flexDirection='column'>
                  <Input
                    errorMessage={errors.message as string | undefined}
                    multiline
                    rows={3}
                    type='textarea'
                    name='message'
                    label={t('orderProduct.refundReason')}
                  />
                  <RadioGroup
                    sx={{
                      marginTop: theme.spacing(6),
                      marginBottom: theme.spacing(8),
                    }}
                    aria-labelledby='demo-radio-buttons-group-label'
                    name='type'
                    value={values.type}
                    onChange={(event) =>
                      setFieldValue('type', event.target.value)
                    }>
                    {(!config || config?.allow_offline_refunds) && (
                      <FormControlLabel
                        control={
                          <RadioBtn disabled={paymentType === 'offline'} />
                        }
                        value='automatic'
                        label={
                          <Typography variant='body2'>
                            {t('orderProduct.automaticRefund')}
                          </Typography>
                        }
                      />
                    )}
                    {(!config || config.allow_offline_refunds) && (
                      <FormControlLabel
                        sx={{ fontWeight: 'bold' }}
                        control={<RadioBtn />}
                        value='manual'
                        label={
                          <Typography variant='body2'>
                            {t('orderProduct.manualRefund')}
                          </Typography>
                        }
                      />
                    )}
                  </RadioGroup>
                </Box>
              </Box>
            </Box>
          </Form>
        </Modal>
      )}
    </Formik>
  )
}
